/**
 * Tiny CSV helpers — no dependency. Handle quoted cells, commas, and newlines
 * within quoted values. Good enough for entity import/export round-trips.
 */

export interface CsvColumn<T> {
  /** Column header shown in the CSV. */
  header: string;
  /** Extract value from a row for export. */
  get: (row: T) => string | number | boolean | null | undefined;
  /** Parse a cell string into the entity field for import. Defaults to raw string. */
  set?: (value: string, row: Record<string, string>) => unknown;
}

const escapeCell = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

/** Build a CSV string from rows + column config. */
export function rowsToCsv<T>(rows: T[], columns: CsvColumn<T>[]): string {
  const header = columns.map((c) => escapeCell(c.header)).join(',');
  const body = rows
    .map((r) => columns.map((c) => escapeCell(c.get(r))).join(','))
    .join('\r\n');
  return body ? `${header}\r\n${body}` : header;
}

/** A CSV with just the headers — used for the "Download template" action. */
export function headerOnlyCsv<T>(columns: CsvColumn<T>[]): string {
  return columns.map((c) => escapeCell(c.header)).join(',');
}

/** Trigger a client-side download of the given text. */
export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Parse a CSV string into an array of field-keyed objects.
 * Respects quoted cells, CRLF and LF line endings, and "" escape for inner quotes.
 * Column order in the file is matched by header name (case-sensitive) against `headerKeys`.
 */
export function parseCsv(text: string, headerKeys: string[]): Record<string, string>[] {
  const rows = tokenizeCsv(text.replace(/^\uFEFF/, ''));
  if (rows.length === 0) return [];
  const headers = rows[0].map((h) => h.trim());
  const keyByIndex = headers.map((h) => {
    const match = headerKeys.find((k) => k.toLowerCase() === h.toLowerCase());
    return match ?? h;
  });
  return rows.slice(1)
    .filter((r) => r.some((c) => c.trim() !== ''))
    .map((r) => {
      const obj: Record<string, string> = {};
      keyByIndex.forEach((key, i) => { obj[key] = (r[i] ?? '').trim(); });
      return obj;
    });
}

function tokenizeCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  let i = 0;
  const n = text.length;
  while (i < n) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { cell += '"'; i += 2; continue; }
        inQuotes = false; i++; continue;
      }
      cell += c; i++; continue;
    }
    if (c === '"') { inQuotes = true; i++; continue; }
    if (c === ',') { row.push(cell); cell = ''; i++; continue; }
    if (c === '\r') { i++; continue; }
    if (c === '\n') { row.push(cell); cell = ''; rows.push(row); row = []; i++; continue; }
    cell += c; i++;
  }
  // Flush trailing cell/row
  row.push(cell);
  if (row.some((v) => v !== '')) rows.push(row);
  return rows;
}
